import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
  isAnyOf,
} from "@reduxjs/toolkit";
import { camelize } from "humps";
import { HYDRATE } from "next-redux-wrapper";
import { normalize } from "normalizr";
import { format, isValid } from "date-fns";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { HotelPlan } from "admin/types/hotelPlan";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { parseDate } from "admin/utils/dateUtil";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import {
  fetchHotel,
  fetchHotelIfNeeded,
} from "admin/features/hotel/hotelSlice";
import { objectToFormData } from "admin/utils/objectUtil";
import { hotelSchema } from "admin/schemas/hotelSchema";
import { HotelPlanStatuses } from "admin/constants/hotelPlanStatuses";

const key = "hotelPlan";
const adapter = createEntityAdapter<HotelPlan.Plan>();

const initialStateValues: HotelPlan.HotelPlanState = {
  hotelPlan: null,
  errors: null,
  loading: false,
  showAll: false,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/

export const postHotelPlan = createAsyncThunk<
  any,
  { context: PageContext.PageContext; hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotelPlan`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/plans.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/plans.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "hotel_plan");
    const result = await createWrapper({ isMultipart: true }).post(
      url,
      formData,
    );
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors || { messages: [err.statusText] });
  }
});

export const putHotelPlan = createAsyncThunk<
  any,
  {
    context: PageContext.PageContext;
    id: number;
    hotelId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelPlan`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/plans/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/plans/${id}.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "hotel_plan");
    const result = await createWrapper({ isMultipart: true }).put(
      url,
      formData,
    );
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors || { messages: [err.statusText] });
  }
});

export const putHotelPlanOrder = createAsyncThunk<
  any,
  {
    context: PageContext.PageContext;
    id: number;
    hotelId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelPlanOrder`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/plans/${id}/order.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/plans/${id}/order.json`;
    const result = await createWrapper().put(url, { hotel_plan: params });
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors || { messages: [err.statusText] });
  }
});

export const deleteHotelPlanImage = createAsyncThunk<
  { hotelPlanId?: number; id: number },
  { context: PageContext.PageContext; hotelPlanId?: number; id: number },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/deleteHotelPlanImage`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelPlanId, id } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_plan_images/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_plan_images/${id}.json`;
    await createWrapper().delete(url);
    return { hotelPlanId: hotelPlanId, id: id };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelPlanImageOrder = createAsyncThunk<
  any,
  {
    context: PageContext.PageContext;
    id: number;
    hotelPlanId: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelPlanImageOrder`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelPlanId, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_plans/${hotelPlanId}/images/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_plans/${hotelPlanId}/images/${id}.json`;
    const result = await createWrapper().put(url, { hotel_plan_image: params });
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors || { messages: [err.statusText] });
  }
});

/** slice **/
export const hotelPlanSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setShowAll: (state, action) => {
      state.showAll = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotelPlan };
      })
      .addCase(postHotelPlan.fulfilled, (state, action) => {
        if (action.payload.entities.hotelPlans) {
          adapter.setAll(state, action.payload.entities.hotelPlans);
        }
      })
      .addCase(putHotelPlan.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.entities.hotelPlans);
      })
      .addCase(putHotelPlanOrder.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.entities.hotelPlans);
      })
      .addCase(deleteHotelPlanImage.fulfilled, (state, action) => {
        const { hotelPlanId, id } = action.payload;
        if (state.entities[hotelPlanId]) {
          state.entities[hotelPlanId].images = state.entities[
            hotelPlanId
          ].images.filter((image) => image.id !== id);
        }
      })
      .addCase(putHotelPlanImageOrder.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.entities.hotelPlans);
      })
      .addMatcher(
        isAnyOf(fetchHotel.fulfilled, fetchHotelIfNeeded.fulfilled),
        (state, action) => {
          adapter.setAll(state, action.payload.entities.hotelPlans ?? []);
        },
      )
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: hotelPlanByIdSelector,
  selectIds: hotelPlanIdsSelector,
  selectEntities: hotelPlanEntitiesSelector,
  selectAll: hotelPlansSelector,
  selectTotal: totalHotelPlansSelector,
} = adapter.getSelectors((state: RootState) => state.hotelPlan);

const stateSelector = (state: RootState) => state[key];

function compareByOrder(a, b) {
  const aOrder = a.order || 0;
  const bOrder = b.order || 0;
  if (aOrder > bOrder) return 1;
  if (aOrder < bOrder) return -1;
  return b.id - a.id;
}

export const hotelPlansByIdsSelector = createSelector(
  [hotelPlanEntitiesSelector, stateSelector, (state, ids: number[]) => ids],
  (entities, state, ids) => {
    if (ids.length === 0) {
      return [];
    }
    const hotelPlans = ids.map((id) => entities[id]).sort(compareByOrder);
    if (state.showAll) {
      return hotelPlans;
    }
    return hotelPlans.filter(
      (hotelPlan) => hotelPlan.status !== HotelPlanStatuses.deleted,
    );
  },
);

export const showAllSelector = createSelector(
  stateSelector,
  (state) => state.showAll,
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

/** action export **/
export const { setShowAll } = hotelPlanSlice.actions;

export const convertHotelPlanFormValuesToParams = (
  supportedLanguages: any,
  data: any,
  isNew = false,
) => {
  const params = { ...data };
  if (params.images) {
    params.images = params.images.map((image) => {
      return { value: image };
    });
  }
  if (params.server_images) {
    // 複製による新規作成時は画像URLを持つ場合があるためURLも送信する
    if (isNew) {
      params.remote_image_urls = params.server_images.map((image) => {
        return image.url;
      });
    }
    delete params.server_images;
  }
  if (!params.option_ids || params.option_ids.length === 0) {
    params.is_empty_option_ids = true;
  }
  if (!params.room_ids || params.room_ids.length === 0) {
    params.is_empty_room_ids = true;
  }
  const credit_card_payment_required = params.credit_card_payment_required;
  supportedLanguages.map((lang) => {
    if (
      credit_card_payment_required &&
      credit_card_payment_required.includes(lang)
    ) {
      params[`credit_card_payment_required_${lang}`] = true;
    } else {
      params[`credit_card_payment_required_${lang}`] = false;
    }
  });

  // formDataがnullとundefinedに対応してないので、空文字を入れる
  if (!params.accommodatable_start_date) {
    params.accommodatable_start_date = "";
  }
  if (!params.accommodatable_end_date) {
    params.accommodatable_end_date = "";
  }
  if (!params.bookable_start_at) {
    params.bookable_start_at = "";
  }
  if (!params.bookable_end_at) {
    params.bookable_end_at = "";
  }
  if (!params.bookable_start_time_from_checkin) {
    params.bookable_start_time_from_checkin = "";
  }
  if (!params.bookable_end_time_from_checkin) {
    params.bookable_end_time_from_checkin = "";
  }
  // hidden_localesは空にする場合キーを削除する
  if (!params.hidden_locales || params.hidden_locales.length === 0) {
    delete params.hidden_locales;
  }
  return params;
};

export const convertHotelPlanToFormValues = (supportedLanguages, data: any) => {
  const values = { ...data };
  if (values.options) {
    values.option_ids = values.options.map((option) =>
      String(option.planOptionId),
    );
    delete values.amenities;
  }
  if (values.checkoutAvailableTo) {
    const checkoutAvailableToArray = values.checkoutAvailableTo.split(":");
    values.to_checkout_hour = checkoutAvailableToArray[0];
    values.to_checkout_minute = checkoutAvailableToArray[1];
  } else {
    values.to_checkout_hour = null;
    values.to_checkout_minute = null;
  }
  if (values.checkinAvailableTo) {
    const checkinAvailableToArray = values.checkinAvailableTo.split(":");
    values.to_checkin_hour = checkinAvailableToArray[0];
    values.to_checkin_minute = checkinAvailableToArray[1];
  } else {
    values.to_checkin_hour = null;
    values.to_checkin_minute = null;
  }
  if (values.checkinAvailableFrom) {
    const checkinAvailableFromArray = values.checkinAvailableFrom.split(":");
    values.from_checkin_hour = checkinAvailableFromArray[0];
    values.from_checkin_minute = checkinAvailableFromArray[1];
  } else {
    values.from_checkin_hour = null;
    values.from_checkin_minute = null;
  }
  if (values.roomIds) {
    values.room_ids = values.roomIds.map((roomId) => String(roomId));
    delete values.roomIds;
  }
  if (values.bookableStartAt) {
    const startAtDate = parseDate(values.bookableStartAt);
    if (isValid(startAtDate)) {
      values.bookableStartAt = format(startAtDate, "yyyy-MM-dd HH:mm");
    }
  }
  if (values.bookableEndAt) {
    const endAtDate = parseDate(values.bookableEndAt);
    values.bookableEndAt = format(endAtDate, "yyyy-MM-dd HH:mm");
  }
  // すでに設定されているimagesはリクエストに含めないため別名で保持する
  if (values.images) {
    // orderでソートする
    const images = [...values.images];
    images.sort((a, b) => a.order - b.order);
    values.server_images = images;
    delete values.images;
  }
  values.creditCardPaymentRequired = [];
  supportedLanguages.map((langauge) => {
    if (values[camelize(`credit_card_payment_required_${langauge}`)]) {
      values.creditCardPaymentRequired.push(langauge);
    }
  });
  return values;
};
